<template>
  <aside class="summary-panel" data-testid="summary-side-panel">
    <div class="summary-panel__header">
      <h2 class="summary-panel__title">Summary</h2>
      <div v-if="ownerControls && description" class="summary-panel__actions">
        <div class="summary-panel__actions-left">
          <Button
            v-if="!summaryContentRef?.editMode"
            type="link"
            variant="gray"
            icon="general.edited"
            tooltip="Edit summary"
            data-testid="summary-edit-btn"
            @click="summaryContentRef?.enterEditMode()"
          />
          <Feedback
            v-if="showFeedback && !summaryContentRef?.editMode"
            :feedback="feedbackState"
            :aiProvider="descriptionProvider"
            :additionalTrackingProps="isSmartActionDescription ? { smart_action_type: smartActionType } : {}"
            @feedback="handleFeedback"
          />
        </div>
        <Button
          v-if="!summaryContentRef?.editMode"
          type="solid"
          variant="blue"
          icon="general.generalExport"
          text="Export summary"
          data-testid="summary-export-btn"
          @click="openExportModal"
        />
      </div>
    </div>
    <div class="summary-panel__content">
      <SummaryContent ref="summaryContentRef" />
    </div>
  </aside>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { Button } from '@/storybook';
import Feedback from '@/storybook/templates/smart_actions/Feedback.vue';
import SummaryContent from './SummaryContent.vue';
import { useViewerItemStore } from '../store/useViewerItemStore';
import { useSummaryFeedback } from '../composables/useSummaryFeedback';
import { useItemActions } from '../composables/useItemActions';

withDefaults(
  defineProps<{
    ownerControls?: boolean;
  }>(),
  { ownerControls: false }
);

const store = useViewerItemStore();
const { openShareModalAction } = useItemActions();
const { description } = storeToRefs(store);
const summaryContentRef = ref<InstanceType<typeof SummaryContent> | null>(null);
const { descriptionProvider, feedbackState, handleFeedback, isSmartActionDescription, showFeedback, smartActionType } = useSummaryFeedback();

const openExportModal = () => {
  openShareModalAction({ modalProps: { selectedTab: '#export' } });
};
</script>

<style scoped>
.summary-panel {
  @apply flex flex-col w-full h-full max-h-full bg-white dark:bg-zinc-800 rounded-10 border border-zinc-200 dark:border-zinc-700 overflow-hidden;
  min-height: 0;
}

.summary-panel__header {
  @apply flex items-center justify-between gap-2 px-4 sm:px-5 py-3 border-b border-zinc-200 dark:border-zinc-700;
}

.summary-panel__title {
  @apply text-xl font-bold text-zinc-900 dark:text-zinc-100 leading-tight truncate;
}

.summary-panel__actions,
.summary-panel__actions-left {
  @apply flex items-center gap-1 sm:gap-2;
}

.summary-panel__content {
  @apply flex-grow min-h-0 overflow-y-auto px-4 sm:px-5 py-4;
  scrollbar-width: thin;
  scrollbar-color: rgb(161 161 170) transparent;
}

.dark .summary-panel__content {
  scrollbar-color: rgb(82 82 91) transparent;
}

.summary-panel__content::-webkit-scrollbar {
  width: 8px;
}

.summary-panel__content::-webkit-scrollbar-track {
  background: transparent;
}

.summary-panel__content::-webkit-scrollbar-thumb {
  background-color: rgb(161 161 170);
  border-radius: 4px;
}

.dark .summary-panel__content::-webkit-scrollbar-thumb {
  background-color: rgb(82 82 91);
}
</style>
